import React from 'react'
import { getPrepCard } from '../cards/preparation/prepCardDefinitions.js'
import { getCard } from '../cardLibrary/cardDefinitions.js'

const GOAL_TYPE_LABELS = { delivery: 'Delivery goals', development: 'Development goals' }

function resolveCard(id) {
  return getPrepCard(id) || getCard(id)
}

function summarizeContent(card, content) {
  if (!content) return null
  if (typeof content === 'string') return content.trim() || null
  const type = card?.type
  if (type === 'bullet-list' || type === 'bullet-list-enriched' || type === 'bullet-list-numbered') {
    const items = content.items
    if (!items || items.length === 0) return null
    return items.map((item) => (typeof item === 'string' ? item : item.text || '')).filter(Boolean).join(', ') || null
  }
  if (type === 'bullet-list-kudos') {
    const items = content.items
    if (!items || items.length === 0) return null
    return items.map((item) => item.who || '').filter(Boolean).join(', ') || null
  }
  if (type === 'kudos') {
    const items = content.items
    if (!items || items.length === 0) return null
    return items.map((item) => item.name || '').filter(Boolean).join(', ') || null
  }
  if (type === 'quick-pulse') {
    return content != null ? String(content) : null
  }
  if (type === 'selection-note') {
    const sels = content.selections
    if (!sels || sels.length === 0) return null
    return sels.join(', ')
  }
  if (type === 'reflection') {
    if (typeof content === 'string') return content.trim() || null
    return Object.values(content).filter(Boolean).join(', ') || null
  }
  return null
}

export function PrintView({ session, layout, sections, goals = [] }) {
  const createdDate = new Date(session.createdAt).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })

  const scheduledDate = session.scheduledAt
    ? new Date(session.scheduledAt).toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  const filledCards = layout
    .map((id) => ({ id, card: resolveCard(id), content: sections[id] }))
    .filter(({ card, content }) => card && summarizeContent(card, content))
    .map(({ id, card, content }) => ({ id, card, summary: summarizeContent(card, content) }))

  const activeGoals = goals.filter((g) => g.status === 'active')

  return (
    <div className="print-view">
      <div className="print-header">
        <div className="print-brand">
          <img src="/logo-mark.svg" alt="" width={18} height={18} />
          <span className="print-brand-name">Soundcheck</span>
        </div>
        <div className="print-meta">
          <h1 className="print-title">1:1 Prep</h1>
          <div className="print-dates">
            <span>Created {createdDate}</span>
            {scheduledDate && <span className="print-date-sep">·</span>}
            {scheduledDate && <span>Scheduled {scheduledDate}</span>}
          </div>
        </div>
        <div className="print-status-badge">
          {session.status === 'completed' ? 'Completed' : 'Draft'}
        </div>
      </div>

      {activeGoals.length > 0 && (
        <div className="print-goals">
          <h2 className="print-goals-heading">Goals</h2>
          {['delivery', 'development'].map((type) => {
            const items = activeGoals.filter((g) => g.goalType === type)
            if (!items.length) return null
            return (
              <div key={type} className="print-goals-group">
                <h3 className="print-goals-type">{GOAL_TYPE_LABELS[type]}</h3>
                <ul className="print-goals-list">
                  {items.map((g) => (
                    <li key={g.goalId}>
                      {g.title}
                      {g.timeHorizon && <span className="print-goal-horizon"> · {g.timeHorizon}</span>}
                      <span className="print-goal-progress"> {g.progress}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}

      <div className="print-sections">
        {filledCards.map(({ id, card, summary }) => (
          <div key={id} className="print-section">
            <h2 className="print-section-heading">
              {card.icon && <span className="print-section-icon">{card.icon}</span>}
              {card.label}
            </h2>
            <p className="print-section-body">{summary}</p>
          </div>
        ))}
      </div>

      <div className="print-footer">Soundcheck — Prepare. Align. Grow.</div>
    </div>
  )
}
